import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import TeamProfile from '../../../components/discover/aivy-pdf-reports/components/teamProfile'
import { Heading } from '../../../components/ui/Heading'
import trackEvents from '../../../constants/track-events'
import { usePartnerContext } from '../../../context'
import { trackEvent } from '../../../helpers/analytics'
import { useDiscover } from '../hooks/useDiscover'
import { useTeamRequirementOverlay } from '../hooks/useTeamRequirementOverlay'
import { getProfileOptions } from '../utils'
import DiscoverSwitch from './DiscoverSwitch'
import EnableFlexSwitch from './EnableFlexSwitch'
import ProfileDropdown from './ProfileDropdown'
import TeamProfileLegend from './teamProfile/TeamProfileLegend'

const PREFIX = 'discover.team_profile_section.requirement_overlay'

export default function TeamProfileSection({
  teamProfile,
  career,
  className,
  selectedCareerIds,
  isLoading,
  isLoadingCareer
}) {
  const { t } = useTranslation()
  const {
    selectedResult,
    setSelectedResult,
    isFlexibleMatchingEnabled,
    setIsFlexibleMatchingEnabled,
    isRequirementOverlayHidden,
    setIsRequirementOverlayHidden
  } = useDiscover()
  const { partner } = usePartnerContext()

  const profileOptions = useMemo(() => getProfileOptions(t), [t])

  const careerId = selectedCareerIds[0]
  const requirement = useTeamRequirementOverlay({
    career,
    careerId,
    reportSettings: partner?.app_settings?.report_v2_settings,
    dimensions: teamProfile?.dimensions_with_text_modules,
    isHidden: isRequirementOverlayHidden
  })

  // Schalter und Legende laden gemeinsam, sonst laufen die Skeletons auseinander
  const isHeaderLoading = isLoading || (!!careerId && isLoadingCareer)
  // Ohne Skalen im Team (z. B. Query fehlgeschlagen) gibt es nichts zu erklären
  const showLegend = isHeaderLoading || requirement.hasScales
  const showRequirementSwitch =
    !!careerId && (isHeaderLoading || requirement.hasScales)

  const handleChangeRequirementVisible = (visible) => {
    trackEvent(trackEvents.DISCOVER_TOGGLE_TEAM_PROFILE_REQUIREMENT, {
      visible
    })
    setIsRequirementOverlayHidden(!visible)
  }

  return (
    <div className={clsx('w-full rounded-lg bg-white py-6', className)}>
      {isLoading && (
        <div className='mb-4 flex justify-center'>
          <span className='text-sm text-darkBlack'>
            {t('discover.team_profile_section.isLoading')}
          </span>
        </div>
      )}
      <div className='border-b border-lightGrey px-6 pb-6'>
        <div className='flex flex-row flex-wrap items-start justify-between gap-x-6 gap-y-3'>
          <div>
            <Heading level={6} className={'text-primaryBlue'}>
              {isLoading ? (
                <Skeleton />
              ) : (
                t('discover.talent_match_section.title')
              )}
            </Heading>
            <ProfileDropdown
              className='mt-1'
              selectedProfile={selectedResult}
              profileOptions={profileOptions}
              onProfileChange={setSelectedResult}
              isLoading={isLoading}
            />
          </div>
          {careerId && (
            <div
              role='group'
              aria-label={t(`${PREFIX}.view_options`)}
              className='ml-auto flex flex-row flex-wrap items-center justify-end gap-x-6 gap-y-2 xl:gap-x-4'
            >
              {/* Ansichtsoption links, der Modus Flexibles Matching bleibt wie
                  in allen Auswertungen ganz rechts, auch wenn die Kopfzeile
                  umbricht. Die Trennlinie erst ab xl, darunter kann die Gruppe
                  selbst umbrechen und die Linie hinge am Zeilenende. */}
              {showRequirementSwitch && (
                <>
                  <DiscoverSwitch
                    label={t(`${PREFIX}.toggle`)}
                    checked={requirement.isVisible}
                    onChange={handleChangeRequirementVisible}
                    disabled={!isHeaderLoading && !requirement.hasBands}
                    disabledReason={t(`${PREFIX}.no_bands`)}
                    isLoading={isHeaderLoading}
                  />
                  <span
                    aria-hidden='true'
                    className='hidden h-5 w-px bg-lightGrey xl:block'
                  />
                </>
              )}
              <EnableFlexSwitch
                enabled={isFlexibleMatchingEnabled}
                setEnabled={setIsFlexibleMatchingEnabled}
                isLoading={isHeaderLoading}
              />
            </div>
          )}
        </div>
        {showLegend && (
          <TeamProfileLegend
            className='mt-4'
            careerTitle={career?.title || career?.name || ''}
            careerCount={selectedCareerIds.length}
            showRequirement={requirement.isVisible}
            hasMandatory={requirement.hasMandatory}
            hasPartialBands={requirement.hasPartialBands}
            isLoading={isHeaderLoading}
          />
        )}
      </div>
      <div className='relative mt-8'>
        <TeamProfile
          data={teamProfile}
          withoutAttachments
          isLoading={isLoading}
          requirementByDimension={requirement.requirementByDimension}
          interestsNote={
            requirement.hasUnbandedInterests
              ? t(`${PREFIX}.interests_hint`)
              : null
          }
        />
        {/* <div className='mt-6 px-6'>
          <PrintButton
            onClick={handleDownloadReport}
            text={t('discover.talent_match_section.print_button')}
          />
        </div> */}
      </div>
    </div>
  )
}

TeamProfileSection.propTypes = {
  teamProfile: PropTypes.object,
  career: PropTypes.object,
  className: PropTypes.string,
  selectedCareerIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  isLoading: PropTypes.bool,
  isLoadingCareer: PropTypes.bool
}
